/**
 * Inversion-of-control seam for the transport layer.
 *
 * `axiosInstance` must trigger session side-effects (clear auth on 401, redirect to
 * the project access-denied route on 403) and read the org's root tenant — but it
 * must NOT import the auth/projects stores directly, or it re-creates the
 * api ⇄ store import cycles. The app registers concrete handlers at startup via
 * `registerSessionPort()`; until then the no-op defaults keep the transport safe.
 */

import type { AccessSurface } from './accessMutations'

export interface SessionPortHandlers {
	/** Clear auth state and redirect to /login on a 401 (no-op when already on /login). */
	onUnauthorized: () => void
	/** Set org/tenant context and redirect to the project access-denied route on a 403. */
	onProjectAccessDenied: (orgId: string, tenantId: string) => void
	/** The org's root tenant id, used as the tenant fallback on org-level routes. */
	getRootTenantId: () => string
	/** A mutation invalidated one or more cached inputs of an access decision. */
	onAccessAffectingMutation: (surfaces: AccessSurface[]) => void
}

const noop = (): void => undefined

const noopHandlers: SessionPortHandlers = {
	onUnauthorized: noop,
	onProjectAccessDenied: noop,
	getRootTenantId: () => '',
	onAccessAffectingMutation: noop,
}

let handlers: SessionPortHandlers = noopHandlers

export const configureSessionPort = (next: SessionPortHandlers): void => {
	handlers = next
}

/** Restore the no-op defaults. Test-only. */
export const resetSessionPort = (): void => {
	handlers = noopHandlers
}

export const sessionPort: SessionPortHandlers = {
	onUnauthorized: () => handlers.onUnauthorized(),
	onProjectAccessDenied: (orgId, tenantId) => handlers.onProjectAccessDenied(orgId, tenantId),
	getRootTenantId: () => handlers.getRootTenantId(),
	onAccessAffectingMutation: surfaces => handlers.onAccessAffectingMutation(surfaces),
}
